const AP_CAR_SVG =
  '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>';

function apBuildVehicleHandle(brand, model) {
  let handle = (brand + ' ' + model).toLowerCase();
  handle = handle.normalize('NFD').replace(/[̀-ͯ]/g, '');
  handle = handle.replace(/&/g, 'and').replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
  while (handle.indexOf('--') !== -1) handle = handle.replace(/--/g, '-');
  return handle.replace(/^-+|-+$/g, '');
}

function apMapEvToVehicles(evList) {
  if (!Array.isArray(evList)) return [];
  const currentYear = new Date().getFullYear();
  const extractYear = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-');
    const year = parseInt(parts[parts.length - 1], 10);
    return Number.isNaN(year) ? null : year;
  };
  const grouped = new Map();
  evList.forEach((entry) => {
    const make = (entry.Vehicle_Make || '').trim();
    const model = (entry.Vehicle_Model || '').trim();
    if (!make || !model) return;
    const version = (entry.Vehicle_Model_Version || '').trim();
    let from = extractYear(entry.Availability_Date_From);
    let to = extractYear(entry.Availability_Date_To);
    if (!from) return;
    if (!to || to < from) to = from;
    if (to === 2025) to = 2026;
    if (to > currentYear) to = currentYear;
    const plug = (entry.Charge_Plug || '').trim();
    const power = parseFloat(entry.Charge_Standard_Power) || 0;
    const phase = parseInt(entry.Charge_Standard_Phase, 10) || 0;
    const key = make + '|' + model + '|' + version;
    if (grouped.has(key)) {
      const existing = grouped.get(key);
      if (from < existing.from) existing.from = from;
      if (to > existing.to) existing.to = to;
      if (power > existing.power) {
        existing.power = power;
        existing.plug = plug;
        existing.phase = phase;
      }
    } else {
      grouped.set(key, { make, model, version, from, to, plug, power, phase });
    }
  });
  const vehicles = [];
  grouped.forEach((entry) => {
    const fullModel = entry.version ? entry.model + ' ' + entry.version : entry.model;
    vehicles.push({
      label: entry.make + ' ' + entry.model + (entry.version ? ' ' + entry.version : ''),
      yearLabel: entry.from + '–' + entry.to,
      powerLabel: entry.power ? (Number.isInteger(entry.power) ? entry.power : entry.power.toFixed(1)) + ' kW' : '',
      plugLabel: entry.plug || '',
      from: entry.from,
      to: entry.to,
      handle: apBuildVehicleHandle(entry.make, fullModel),
      search: (entry.make + ' ' + entry.model + ' ' + entry.version).toLowerCase().replace(/\s+/g, ' ').trim(),
    });
  });
  return vehicles;
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    this.saveRecentSearch(query);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    this.ensureVehicleData();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const params = [
      `q=${encodeURIComponent(searchTerm)}`,
      'resources[type]=query,article,collection,page,product',
      'resources[limit]=6',
      'resources[limit_scope]=each',
      'section_id=predictive-search',
    ].join('&');
    fetch(`${routes.predictive_search_url}?${params}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.injectVehicleMatches(this.searchTerm);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem('amperePointRecentSearches')) || [];
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    term = (term || '').trim();
    if (!term) return;
    try {
      let list = this.getRecentSearches().filter((entry) => entry.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      if (list.length > 5) list = list.slice(0, 5);
      localStorage.setItem('amperePointRecentSearches', JSON.stringify(list));
    } catch (e) {}
  }

  renderEmptyState() {
    const recentLabel = this.dataset.recentLabel;
    const popularLabel = this.dataset.popularLabel;
    const searchUrl = this.dataset.searchUrl || '/search';
    const recent = recentLabel ? this.getRecentSearches() : [];
    const popular = popularLabel
      ? (this.dataset.popularTerms || '')
          .split(',')
          .map((term) => term.trim())
          .filter(Boolean)
      : [];

    if (!recent.length && !popular.length) return;

    const esc = (string) =>
      string.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const chip = (term, kind, index) => {
      const href = `${searchUrl}?q=${encodeURIComponent(term)}&options[prefix]=last`;
      return `<li id="predictive-search-option-${kind}-${index}" class="predictive-search__list-item predictive-search__list-item--chip" role="option" aria-selected="false"><a href="${href}" class="predictive-search__chip link" tabindex="-1">${esc(term)}</a></li>`;
    };

    const group = (label, items, kind) =>
      `<div class="predictive-search__group"><div class="predictive-search__section-head"><h2 class="predictive-search__heading">${esc(
        label
      )}</h2></div><ul class="predictive-search__queries list-unstyled" role="group">${items
        .map((term, index) => chip(term, kind, index + 1))
        .join('')}</ul></div>`;

    let html = '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__empty">';
    if (recent.length) html += group(recentLabel, recent, 'recent');
    if (popular.length) html += group(popularLabel, popular, 'popular');
    html += '</div>';

    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', true);
    this.open();
  }

  ensureVehicleData() {
    if (this._vehiclePromise) return this._vehiclePromise;
    const urls = (this.dataset.vehicleDataUrls || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);
    if (!urls.length) {
      this._vehiclePromise = Promise.resolve();
      return this._vehiclePromise;
    }
    this._vehiclePromise = Promise.all(
      urls.map((url) =>
        fetch(url)
          .then((response) => response.json())
          .catch(() => [])
      )
    )
      .then((lists) => {
        const raw = lists.flat().filter(Boolean);
        const byId = new Map();
        raw.forEach((item) => {
          if (item && typeof item.Vehicle_ID !== 'undefined' && !byId.has(item.Vehicle_ID)) {
            byId.set(item.Vehicle_ID, item);
          }
        });
        this.vehicleData = apMapEvToVehicles(Array.from(byId.values()));
      })
      .catch(() => {
        this.vehicleData = [];
      })
      .then(() => {
        if (this.isOpen && this.getQuery()) this.injectVehicleMatches(this.getQuery());
      });
    return this._vehiclePromise;
  }

  matchVehicles(query) {
    if (!this.vehicleData || !this.vehicleData.length) return [];
    const normalized = (query || '').toLowerCase().trim();
    if (normalized.length < 2) return [];
    const tokens = normalized.split(/\s+/).filter(Boolean);
    const years = tokens.filter((token) => /^\d{4}$/.test(token));
    const words = tokens.filter((token) => !/^\d{4}$/.test(token));
    if (!words.length) return [];
    const matches = this.vehicleData.filter((vehicle) => {
      if (!words.every((token) => vehicle.search.indexOf(token) !== -1)) return false;
      return years.every((year) => {
        const value = parseInt(year, 10);
        return (value >= vehicle.from && value <= vehicle.to) || vehicle.search.indexOf(year) !== -1;
      });
    });
    matches.sort((a, b) => a.label.length - b.label.length || a.label.localeCompare(b.label));
    return matches.slice(0, 4);
  }

  injectVehicleMatches(query) {
    const previous = this.querySelector('.predictive-search__vehicles');
    if (previous) previous.remove();
    const matches = this.matchVehicles(query);
    if (!matches.length) return;
    const results = this.querySelector('#predictive-search-results');
    if (!results) return;
    let wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      results.insertAdjacentHTML(
        'afterbegin',
        '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper"></div>'
      );
      wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    }
    let label = this.dataset.vehicleLabel || 'Twój pojazd';
    if (/ranslation missing/i.test(label)) label = 'Twój pojazd';
    const esc = (string) =>
      string.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    let html =
      '<div class="predictive-search__group predictive-search__vehicles"><div class="predictive-search__section-head"><h2 class="predictive-search__heading">' +
      esc(label) +
      '</h2></div><ul class="predictive-search__vehicle-list list-unstyled" role="group">';
    matches.forEach((vehicle, index) => {
      let spec = '';
      if (vehicle.powerLabel) {
        spec =
          '<span class="predictive-search__vehicle-spec"><span class="predictive-search__vehicle-power">' +
          esc(vehicle.powerLabel) +
          '</span>' +
          (vehicle.plugLabel
            ? '<span class="predictive-search__vehicle-plug">' + esc(vehicle.plugLabel) + '</span>'
            : '') +
          '</span>';
      }
      html +=
        '<li id="predictive-search-option-vehicle-' +
        (index + 1) +
        '" class="predictive-search__list-item" role="option" aria-selected="false"><a href="' +
        ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') +
        'collections/' +
        vehicle.handle +
        '" class="predictive-search__vehicle-card link" tabindex="-1"><span class="predictive-search__vehicle-icon" aria-hidden="true">' +
        AP_CAR_SVG +
        '</span><span class="predictive-search__vehicle-text"><span class="predictive-search__vehicle-name">' +
        esc(vehicle.label) +
        '</span><span class="predictive-search__vehicle-year">' +
        esc(vehicle.yearLabel) +
        '</span></span>' +
        spec +
        '</a></li>';
    });
    html += '</ul></div>';
    wrapper.insertAdjacentHTML('afterbegin', html);
  }
}

customElements.define('predictive-search', PredictiveSearch);